import React, { useState } from "react";
import { Box, ButtonBase } from "@mui/material";
import LockOpenOutlined from "@mui/icons-material/LockOpenOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";

import { monoFontFamily } from "../../theme/tokens";

export interface MaskedTaxIdProps {
  /** The value as received. Never modified; masking happens only in what is rendered. */
  value: string | null | undefined;
  /** Whether this person may reveal the full value (show the Show/Hide button). */
  canReveal: boolean;
  /** Controlled revealed state. Omit to let each instance keep its own. */
  revealed?: boolean;
  /** Called by Show/Hide. Required when `revealed` is controlled. */
  onToggle?: () => void;
}

const MASK = "••••";

/**
 * A tax ID shown masked by default: lock icon + "•••• 1234" (last four characters), or
 * "Hidden" when there is no value. When `canReveal`, a small Show/Hide button reveals
 * the value for this row only. Never log or store the value.
 */
export function MaskedTaxId({
  value,
  canReveal,
  revealed,
  onToggle,
}: MaskedTaxIdProps) {
  const [localRevealed, setLocalRevealed] = useState(false);
  const isControlled = revealed !== undefined;
  const hasValue = typeof value === "string" && value.trim() !== "";
  const showFull =
    canReveal && hasValue && (isControlled ? revealed : localRevealed);

  const handleToggle = () => {
    if (onToggle) onToggle();
    if (!isControlled) setLocalRevealed((prev) => !prev);
  };

  let text: string;
  if (!hasValue) text = "Hidden";
  else if (showFull) text = value as string;
  else text = `${MASK} ${(value as string).slice(-4)}`;

  const Icon = showFull ? LockOpenOutlined : LockOutlined;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.5,
        fontFamily: monoFontFamily,
        fontSize: "0.75rem",
        color: showFull ? "text.primary" : "text.secondary",
        whiteSpace: "nowrap",
      }}
    >
      <Icon aria-hidden sx={{ fontSize: 15 }} />
      <span>{text}</span>
      {canReveal && hasValue && (
        <ButtonBase
          onClick={handleToggle}
          aria-pressed={showFull}
          aria-label={showFull ? "Hide tax ID" : "Show tax ID"}
          sx={(theme) => ({
            fontFamily: theme.typography.fontFamily,
            fontSize: "0.78125rem",
            fontWeight: 500,
            color: theme.palette.primary.main,
            px: 0.5,
            borderRadius: 0.5,
            "&:hover": { textDecoration: "underline" },
          })}
        >
          {showFull ? "Hide" : "Show"}
        </ButtonBase>
      )}
    </Box>
  );
}

export default MaskedTaxId;
